import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useContext, useEffect, useState } from 'react';
import Iconify from 'src/components/iconify';
import { getInpersonInterviewDetails } from 'src/services/candidate.service';
import { fDateTime } from 'src/utils/format-time';
import PropTypes from 'prop-types';
import { REJECTED } from '../job-candidate/ai-recommendation-status-constants';
import {
  AI_INTERVIEW_EVALUATED,
  CANDIDATE_HIRED,
  CANDIDATE_REJECTED,
  IN_PERSON_INTERVIEW_SCHEDULED,
} from '../job-candidate/job-candidate-status-constants';
import {
  AI_INTERVIEW,
  CODING_INTERVIEW_SUBMITTED,
  HIRED,
  INPERSON_INTERVIEW,
} from '../job-candidate/screening-status-constants';
import CreateInpersonInterviewForm from './create-inperson-form';
import FallbackUI from './fallback-ui';
import FeedbackForm from './feedback-form';
import ProfileLoader from './loader';
import { CandidateProfileContext } from './context/candidate-profile-context';

const useStyles = makeStyles(theme => ({
  addInterviewBtn: {
    background: theme.palette.background.primaryAccent2,
    color: theme.palette.content.primaryBlack1,
    '&:hover': {
      background: theme.palette.background.primaryAccent2,
    },
  },
  mainItemdiv: {
    padding: 20,
    gap: 16,
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    borderRadius: 6,
  },
  fiftyPercent: {
    width: '50%',
  },
  greenDiv: {
    background: theme.palette.background.primaryGreen2,
    color: theme.palette.background.primaryGreen1,
    padding: '10px 16px',
    flexDirection: 'row',
    borderRadius: 4,
    gap: 5,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  tickIcon: {
    color: theme.palette.background.primaryGreen1,
  },
}));

const InPersonInterviewItem = ({
  item,
  jobId,
  userId,
  feedbackFormOpen,
  setFeedbackFormOpen,
  index,
}) => {
  // hooks
  const classes = useStyles();

  return (
    <Stack className={classes.mainItemdiv}>
      <Stack direction="row">
        <Typography variant="input" className={classes.fiftyPercent}>
          Technical round
        </Typography>
        <Typography variant="input" className={classes.fiftyPercent}>
          {fDateTime(item?.scheduled_at)}
        </Typography>
      </Stack>
      <Stack direction="row">
        <Typography variant="input" className={classes.fiftyPercent}>
          Interview
        </Typography>
        <Stack className={classes.fiftyPercent} spacing="6px">
          <Typography variant="input">{item?.interviewer_name}</Typography>
          <Typography
            variant="input"
            sx={{ color: theme => theme.palette.content.primaryBlack3 }}
          >
            {item?.interviewer_email}
          </Typography>
        </Stack>
      </Stack>
      {item?.guest_email_list && item?.guest_email_list?.length > 0 && (
        <Stack direction="row">
          <Typography variant="input" className={classes.fiftyPercent}>
            Guests
          </Typography>
          <Stack spacing="12px">
            {item?.guest_email_list.map(guest => (
              <Typography variant="input">{guest}</Typography>
            ))}
          </Stack>
        </Stack>
      )}
      {!item?.interview_feedback && (
        <Stack className={classes.greenDiv}>
          <Iconify
            width={16}
            height={16}
            className={classes.tickIcon}
            icon="tabler:circle-check-filled"
          />
          <Typography variant="caption">
            Interview is scheduled and google calendar invite is sent to all the
            stakeholders.
            <span
              style={{ marginRight: '5px', fontSize: '12px', color: 'black' }}
            >
              See in google calendar
            </span>
          </Typography>
        </Stack>
      )}
      {item?.interview_feedback && (
        <Stack spacing="10px" sx={{ marginTop: '10px' }}>
          <Typography
            variant="input"
            sx={{ fontWeight: 600 }}
            className={classes.fiftyPercent}
          >
            Feedback...
          </Typography>
          <Typography variant="caption" sx={{ lineHeight: 'normal' }}>
            {item?.interview_feedback}
          </Typography>
        </Stack>
      )}
      {!item?.interview_feedback && !feedbackFormOpen[index] && (
        <Button
          onClick={() => {
            setFeedbackFormOpen(prev => {
              const dum = [...prev];
              dum[index] = true;
              return dum;
            });
          }}
          variant="contained"
        >
          Add feedback
        </Button>
      )}
      {feedbackFormOpen[index] && (
        <FeedbackForm
          setFeedbackFormOpen={setFeedbackFormOpen}
          jobId={jobId}
          userId={userId}
          inpersonInterviewId={item?.inperson_interview_id}
        />
      )}
    </Stack>
  );
};

const InPersonGroup = ({ jobId }) => {
  const userId = useContext(CandidateProfileContext)?.obj?.candidate_info
    ?.user_id;
  const jobCandidateStage = useContext(CandidateProfileContext)?.obj
    ?.job_candidate_stage;
  const jobCandidateStatus = useContext(CandidateProfileContext)?.obj
    ?.screening_status;

  const classes = useStyles();

  const [inPersonInterviews, setInPersonInterviews] = useState(null);
  const [formOpen, setFormOpen] = useState(true);
  const [feedbackFormOpen, setFeedbackFormOpen] = useState([]);
  const [inPersonLoader, setInPersonLoader] = useState(true);

  useEffect(() => {
    const caller = () => {
      setInPersonLoader(true);
      getInpersonInterviewDetails(jobId, userId)
        .then(res => {
          if (
            !inPersonInterviews &&
            (res?.body.length > 0 ||
              [HIRED, REJECTED].includes(jobCandidateStage) ||
              [CANDIDATE_HIRED, CANDIDATE_REJECTED].includes(
                jobCandidateStatus,
              ))
          ) {
            setFormOpen(false);
          }
          setInPersonInterviews(res?.body);
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          setInPersonLoader(false);
        });
    };
    caller();
  }, [jobId, userId, formOpen, feedbackFormOpen]);

  // components
  const InPersonGroupSkeleton = (
    <Stack spacing="30px">
      <Skeleton width="100%" height="11px" />
      <Stack
        spacing="20px"
        border={theme => `1px solid ${theme.palette.border.primaryBlack3}`}
        borderRadius="11px"
        padding="20px"
      >
        <Stack spacing="11px">
          <Skeleton width={37} height="9px" />
          <Stack
            border={theme => `1px solid ${theme.palette.border.primaryBlack3}`}
            borderRadius="6px"
            width="100%"
            height="32px"
            justifyContent="center"
            paddingLeft="8px"
          >
            <Skeleton width={116} height={7} />
          </Stack>
        </Stack>
        <Stack spacing="11px">
          <Skeleton width={37} height="9px" />
          <Stack
            border={theme => `1px solid ${theme.palette.border.primaryBlack3}`}
            borderRadius="6px"
            width="100%"
            height="32px"
            justifyContent="center"
            paddingLeft="8px"
          >
            <Skeleton width={72} height={7} />
          </Stack>
        </Stack>
        <Stack spacing="11px">
          <Skeleton width={139} height="9px" />
          <Stack
            border={theme => `1px solid ${theme.palette.border.primaryBlack3}`}
            borderRadius="6px"
            width="100%"
            height="32px"
            justifyContent="center"
            paddingLeft="8px"
          >
            <Skeleton width={116} height={7} />
          </Stack>
        </Stack>
        <Stack spacing="11px">
          <Skeleton width={138} height="9px" />
          <Stack
            border={theme => `1px solid ${theme.palette.border.primaryBlack3}`}
            borderRadius="6px"
            width="100%"
            height="32px"
            justifyContent="center"
            paddingLeft="8px"
          >
            <Skeleton width={116} height={7} />
          </Stack>
        </Stack>
        <Stack spacing="11px">
          <Skeleton width={138} height="9px" />
          <Stack
            border={theme => `1px solid ${theme.palette.border.primaryBlack3}`}
            borderRadius="6px"
            width="100%"
            height="32px"
            justifyContent="center"
            paddingLeft="8px"
          >
            <Skeleton width={116} height={7} />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          width="126px"
          height="36px"
          alignItems="center"
          justifyContent="center"
          spacing="8px"
          bgcolor="#FAFAFA"
          borderRadius="21px"
        >
          <Iconify
            icon="tabler:users"
            width={20}
            sx={{ color: 'border.primaryBlack3' }}
          />
          <Skeleton width={78} height={8} />
        </Stack>
        <Stack direction="row" alignItems="center" width="100%" spacing="12px">
          <Skeleton
            width="100%"
            height="44px"
            sx={{
              backgroundColor: '#F2F2F2',
            }}
          />
          <Skeleton
            width="100%"
            height="44px"
            sx={{
              backgroundColor: theme => theme.palette.border.primaryBlack3,
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <>
      {inPersonLoader && (
        <Stack
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          Loading...
        </Stack>
      )}
      {!inPersonLoader &&
        ([AI_INTERVIEW, INPERSON_INTERVIEW, HIRED, REJECTED].includes(
          jobCandidateStage,
        ) &&
        [
          AI_INTERVIEW_EVALUATED,
          CODING_INTERVIEW_SUBMITTED,
          IN_PERSON_INTERVIEW_SCHEDULED,
          CANDIDATE_HIRED,
          CANDIDATE_REJECTED,
        ].includes(jobCandidateStatus) ? (
          inPersonInterviews && (
            <Stack spacing="20px">
              {!(
                [HIRED, REJECTED].includes(jobCandidateStage) &&
                inPersonInterviews?.length === 0
              ) ? (
                <Typography variant="subheading1">Interview</Typography>
              ) : (
                <FallbackUI content="Interview will be active only after AI interview is completed" />
              )}
              {!formOpen &&
                ![HIRED, REJECTED].includes(jobCandidateStage) &&
                ![CANDIDATE_HIRED, CANDIDATE_REJECTED].includes(
                  jobCandidateStatus,
                ) && (
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon="tabler:plus" />}
                    className={classes.addInterviewBtn}
                    onClick={() => {
                      setFormOpen(true);
                    }}
                  >
                    Add interview
                  </Button>
                )}
              {formOpen && (
                <CreateInpersonInterviewForm
                  jobId={jobId}
                  userId={userId}
                  setFormOpen={setFormOpen}
                />
              )}
              {inPersonInterviews?.map((item, index) => (
                <InPersonInterviewItem
                  key={index}
                  item={item}
                  jobId={jobId}
                  userId={userId}
                  feedbackFormOpen={feedbackFormOpen}
                  setFeedbackFormOpen={setFeedbackFormOpen}
                  index={index}
                />
              ))}
            </Stack>
          )
        ) : (
          <FallbackUI content="Interview will be active only after AI interview is completed" />
        ))}
    </>
  );
};

export default InPersonGroup;

InPersonGroup.propTypes = {
  jobId: PropTypes.string.isRequired,
};

InPersonInterviewItem.propTypes = {
  item: PropTypes.object.isRequired,
  jobId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  feedbackFormOpen: PropTypes.array.isRequired,
  setFeedbackFormOpen: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};
